/**
 * @fileoverview 认证相关工具函数
 * @description 提供登录、登出等认证功能
 * @author wangzhongsheng
 * @version 2.0.0
 */

import { request } from './request.js'
import { getSDKConfig } from './config.js'

/**
 * 退出登录
 * @param {Object} options - 配置选项
 * @param {string} options.streetKey - 街道标识
 * @param {boolean} options.clearLocal - 是否清除本地存储，默认true
 * @param {boolean} options.sendRequest - 是否发送服务器请求，默认true
 * @returns {Promise<Object>} 退出结果
 */
export const logout = async (options = {}) => {
  const {
    clearLocal = true,
    sendRequest = true
  } = options

  try {
    const result = {
      success: true,
      localCleared: false,
      serverNotified: false
    }
    const globalConfig = getSDKConfig()
    // 发送服务器退出请求
    if (sendRequest) {
      try {
        const token = localStorage.getItem(globalConfig.tokenKey)
        if (token) {
          const response = await request.get('/auth/logout')
          result.serverNotified = true
          console.log('服务器退出成功:', response)
        }
      } catch (error) {
        console.warn('服务器退出请求失败:', error)
        // 服务器请求失败不影响本地清除
      }
    }

    // 清除本地存储
    if (clearLocal) {
      localStorage.removeItem(globalConfig.tokenKey)
      localStorage.removeItem(globalConfig.userKey)
      result.localCleared = true
      console.log('本地认证信息已清除')
    }

    return result

  } catch (error) {
    console.error('退出登录失败:', error)
    return {
      success: false,
      error: error.message || '退出登录失败',
      localCleared: false,
      serverNotified: false
    }
  }
}

/**
 * 检查登录状态
 * @returns {Object} 登录状态信息
 */
export const checkLoginStatus = () => {
  const globalConfig = getSDKConfig()
  const token = localStorage.getItem(globalConfig.tokenKey)
  const userInfoStr = localStorage.getItem(globalConfig.userKey)
  
  if (!token) {
    return { isLoggedIn: false, token: null, userInfo: null }
  }

  try {
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null
    return { 
      isLoggedIn: true, 
      token, 
      userInfo 
    }
  } catch (error) {
    console.error('解析用户信息失败:', error)
    // 清除无效的用户信息
    localStorage.removeItem(globalConfig.userKey)
    return { 
      isLoggedIn: !!token, 
      token, 
      userInfo: null 
    }
  }
}

/**
 * 获取当前用户信息
 * @returns {Object|null} 用户信息或null
 */
export const getCurrentUser = () => {
  const { userInfo } = checkLoginStatus()
  return userInfo
}

/**
 * 获取当前token
 * @returns {string|null} token或null
 */
export const getCurrentToken = () => {
  const globalConfig = getSDKConfig()
  return localStorage.getItem(globalConfig.tokenKey)
} 